import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AnyAction, ThunkDispatch } from "@reduxjs/toolkit";
import { WorkflowAutomationId, WorkflowAutomationListItem } from "../../../types";
import {
  fetchAutomations,
  initializeAutomationsList,
  toggleAutomationActive,
  selectAutomations,
  selectListLoading,
  selectTogglingWorkflowId,
  selectWorkflowAutomationsError,
  WorkflowAutomationsRootState,
} from "../../../redux/workflowAutomations";
import { useHasCapabilities } from "../../../hooks";
import { MODIFY_WORKFLOW_AUTOMATION_CAPABILITY_NAME } from "../WorkflowAutomationsRouter";

export interface UseWorkflowAutomationsResult {
  workflows: WorkflowAutomationListItem[];
  loading: boolean;
  error: string | null;
  togglingWorkflowId: WorkflowAutomationId | null;
  toggleActive: (workflowId: WorkflowAutomationId, active: boolean) => void;
}

export function useWorkflowAutomations(): UseWorkflowAutomationsResult {
  const dispatch = useDispatch<ThunkDispatch<WorkflowAutomationsRootState, undefined, AnyAction>>();
  const canModify = useHasCapabilities(MODIFY_WORKFLOW_AUTOMATION_CAPABILITY_NAME, true);
  const workflows = useSelector((state: WorkflowAutomationsRootState) =>
    selectAutomations(state)
  );
  const loading = useSelector((state: WorkflowAutomationsRootState) =>
    selectListLoading(state)
  );
  const togglingWorkflowId = useSelector((state: WorkflowAutomationsRootState) =>
    selectTogglingWorkflowId(state)
  );
  const error = useSelector((state: WorkflowAutomationsRootState) =>
    selectWorkflowAutomationsError(state)
  );

  useEffect(() => {
    if (canModify) {
      dispatch(initializeAutomationsList());
    } else {
      dispatch(fetchAutomations());
    }
  }, [canModify, dispatch]);

  const toggleActive = useCallback(
    (workflowId: WorkflowAutomationId, active: boolean) => {
      dispatch(toggleAutomationActive({ workflowId, active }));
    },
    [dispatch]
  );

  return { workflows, loading, error, togglingWorkflowId, toggleActive };
}
